import { doorColours, doorRules, doorTypeIds } from '@planner/config';
import { isValidReferenceCode } from '@planner/shared';
import { acknowledgementFields, helpTopics, numericFields, steps } from './events.js';

const choices: Record<string, readonly string[]> = {
  door_type: doorTypeIds,
  measurement_mode: ['diy', 'trade'],
  lock_side: ['left', 'right'],
  frame_colour: doorColours.map((colour) => colour.name),
};
const errors: Record<string, string> = {
  catalogue_load: 'Catalogue could not be loaded',
  draft_recovered: 'Saved draft was repaired',
  draft_discarded: 'Saved draft was discarded',
};

function oneOf<T extends string>(value: unknown, values: readonly T[]): value is T {
  return values.some((entry) => entry === value);
}
function integer(value: unknown, minimum: number, maximum: number): value is number {
  return (
    typeof value === 'number' && Number.isSafeInteger(value) && value >= minimum && value <= maximum
  );
}
function money(value: unknown): value is number {
  return (
    typeof value === 'number' &&
    Number.isFinite(value) &&
    value >= 0 &&
    value <= Number.MAX_SAFE_INTEGER / 100
  );
}
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Reconstruct payloads at the provider boundary; TypeScript alone cannot validate runtime data. */
export function serializeEvent(
  event: string,
  value: unknown,
): Record<string, string | number> | undefined {
  if (!isRecord(value)) return undefined;
  const p = value;
  switch (event) {
    case 'planner_started':
      return {};
    case 'planner_step_completed':
      return oneOf(p.step, steps) ? { step: p.step } : undefined;
    case 'configuration_changed': {
      if (oneOf(p.field, [...numericFields, ...acknowledgementFields])) return { field: p.field };
      if (typeof p.field !== 'string' || !Object.hasOwn(choices, p.field)) return undefined;
      return oneOf(p.value, choices[p.field]) ? { field: p.field, value: p.value } : undefined;
    }
    case 'measuring_guide_downloaded':
      return oneOf(p.mode, ['diy', 'trade']) ? { mode: p.mode } : undefined;
    case 'help_opened':
      return oneOf(p.topic, helpTopics) ? { topic: p.topic } : undefined;
    case 'error_occurred':
      return typeof p.context === 'string' &&
        Object.hasOwn(errors, p.context) &&
        p.message === errors[p.context]
        ? { context: p.context, message: errors[p.context] }
        : undefined;
    case 'checkout_clicked':
      return oneOf(p.channel, ['retail', 'trade']) ? { channel: p.channel } : undefined;
    case 'drawing_approved':
      if (p.design_reference === undefined) return {};
  }
  if (typeof p.design_reference !== 'string' || !isValidReferenceCode(p.design_reference))
    return undefined;
  const reference = { design_reference: p.design_reference };
  switch (event) {
    case 'drawing_approved':
    case 'terms_agreed':
    case 'privacy_acknowledged':
    case 'drawing_downloaded':
    case 'cart_emailed':
      return reference;
    case 'cart_line_added':
    case 'cart_line_updated':
    case 'cart_line_removed':
    case 'cart_cleared':
    case 'cart_summary_downloaded': {
      if (
        !integer(p.line_count, event === 'cart_line_removed' ? 0 : 1, doorRules.maxDoorsPerOrder) ||
        !money(p.total) ||
        (p.line_count === 0 && p.total !== 0)
      )
        return undefined;
      const cart = { ...reference, line_count: p.line_count, total: p.total };
      if (event !== 'cart_line_added') return cart;
      if (
        !oneOf(p.door_type, doorTypeIds) ||
        !oneOf(p.frame_colour, choices.frame_colour) ||
        !oneOf(p.measure_mode, ['diy', 'trade']) ||
        !integer(p.quantity, doorRules.quantity.min, doorRules.quantity.max) ||
        !money(p.line_total)
      )
        return undefined;
      return {
        ...cart,
        door_type: p.door_type,
        frame_colour: p.frame_colour,
        measure_mode: p.measure_mode,
        quantity: p.quantity,
        line_total: p.line_total,
      };
    }
    default:
      return undefined;
  }
}
